import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, formatMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, FileText, Anchor, Pencil } from "lucide-react";

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${id}`);
        setInv(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("Failed to fetch PDF");
    }
  };

  if (!inv) {
    return (
      <div className="text-sm text-zinc-500">Loading…</div>
    );
  }

  const isShipping = inv.invoice_type === "shipping";

  return (
    <div className="space-y-6" data-testid="invoice-view-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button onClick={() => navigate("/invoices")} className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-2 flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Back
          </button>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">
            {isShipping ? "Freight Invoice" : "Service Invoice"} · {inv.status}
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight">{inv.invoice_no}</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate(`/invoices/${id}/edit`)} variant="outline" className="rounded-sm h-11 border-zinc-300 font-mono uppercase tracking-widest text-xs" data-testid="edit-invoice">
            <Pencil className="h-4 w-4 mr-2" /> Edit
          </Button>
          <Button onClick={downloadPdf} className="btn-pressable rounded-sm h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs px-5" data-testid="download-pdf">
            <FileText className="h-4 w-4 mr-2" /> Download PDF
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto invoice-sheet p-10 md:p-14 rounded-sm">
        <div className="flex items-start justify-between mb-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="h-8 w-8 grid place-items-center bg-[#F97316] rounded-sm">
                <Anchor className="h-4 w-4 text-black" strokeWidth={2.5} />
              </span>
              <span className="font-heading font-black tracking-tight">OCEANFREIGHT LOGISTICS</span>
            </div>
            <div className="text-xs text-zinc-500">Pier 7, Mumbai Port, India</div>
            <div className="text-xs text-zinc-500">ops@oceanfreight.com</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-3xl font-black tracking-tight">
              {isShipping ? "FREIGHT INVOICE" : "SERVICE INVOICE"}
            </div>
            <div className="font-mono text-sm font-semibold mt-2">{inv.invoice_no}</div>
            <div className="text-xs text-zinc-500 mt-3">Issued: <span className="font-mono">{inv.issue_date}</span></div>
            <div className="text-xs text-zinc-500">Due: <span className="font-mono">{inv.due_date}</span></div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Bill to</div>
            <div className="font-semibold text-zinc-900">{inv.consignee_name}</div>
            <div className="text-xs text-zinc-500 whitespace-pre-line">{inv.consignee_address}</div>
            <div className="text-xs text-zinc-500">{inv.consignee_email}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Consignor</div>
            <div className="font-semibold text-zinc-900">{inv.consignor_name || "—"}</div>
            <div className="text-xs text-zinc-500 whitespace-pre-line">{inv.consignor_address}</div>
          </div>
        </div>

        {isShipping && (
          <div className="mb-8 grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
            <Meta label="Vessel" value={inv.vessel_name} />
            <Meta label="Voyage no" value={inv.voyage_no} mono />
            <Meta label="B/L no" value={inv.bl_no} mono />
            <Meta label="Port of loading" value={inv.port_of_loading} />
            <Meta label="Port of discharge" value={inv.port_of_discharge} />
            <Meta label="Currency" value={inv.currency} mono />
          </div>
        )}

        {isShipping && inv.cargo?.length > 0 && (
          <div className="mb-8">
            <div className="font-heading font-bold mb-3">Cargo</div>
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#09090B] text-white">
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Container</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Type</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Description</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Qty</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Weight (kg)</th>
                </tr>
              </thead>
              <tbody>
                {inv.cargo.map((c, i) => (
                  <tr key={i} className="border-b border-zinc-200">
                    <td className="px-3 py-2 font-mono text-xs">{c.container_no || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{c.container_type || "—"}</td>
                    <td className="px-3 py-2">{c.description}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.quantity}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(c.weight_kg).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mb-8">
          <div className="font-heading font-bold mb-3">{isShipping ? "Freight & charges" : "Services"}</div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#09090B] text-white">
                <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Description</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Qty</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Unit price</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Tax</th>
                <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.items.map((it, i) => {
                const line = Number(it.quantity) * Number(it.unit_price);
                return (
                  <tr key={i} className="border-b border-zinc-200">
                    <td className="px-3 py-2">{it.description}</td>
                    <td className="px-3 py-2 text-right font-mono">{it.quantity}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatMoney(it.unit_price, inv.currency)}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(it.tax_rate)}%</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{formatMoney(line, inv.currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <div className="w-full max-w-xs space-y-2">
            <Row label="Subtotal" value={formatMoney(inv.subtotal, inv.currency)} />
            <Row label={inv.tax_label} value={formatMoney(inv.tax_total, inv.currency)} />
            <Row label="Discount" value={`- ${formatMoney(inv.discount, inv.currency)}`} />
            <div className="flex items-center justify-between py-3 px-4 bg-[#F97316] text-white rounded-sm">
              <span className="font-heading font-bold uppercase tracking-tight">Grand total</span>
              <span className="font-heading font-black text-lg">{formatMoney(inv.grand_total, inv.currency)}</span>
            </div>
          </div>
        </div>

        {inv.notes && (
          <div className="mt-10 pt-6 border-t border-zinc-200">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Notes</div>
            <div className="text-xs text-zinc-600 whitespace-pre-line">{inv.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
      <div className={`text-sm text-zinc-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
